// Detaljvy för en enskild aktie: header, prisgraf (Lightweight Charts), nyckeltal.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import KeyStats from '../components/stock/KeyStats';
import StockChart from '../components/stock/StockChart';
import { type HistoryRange } from '../lib/history';
import { pb } from '../lib/pocketbase';
import { fmtNumber } from '../lib/format';
import type { Stock } from '../types/stock';

const RANGES: HistoryRange[] = ['1m', '3m', '6m', '1y', '2y'];
const RANGE_LABEL: Record<HistoryRange, string> = {
  '1m': '1M',
  '3m': '3M',
  '6m': '6M',
  '1y': '1Y',
  '2y': '2Y',
};

export default function StockDetail() {
  const { ticker: rawTicker } = useParams<{ ticker: string }>();
  const ticker = rawTicker ? decodeURIComponent(rawTicker).toUpperCase() : '';

  const [stock, setStock] = useState<Stock | null>(null);
  const [range, setRange] = useState<HistoryRange>('6m');
  const [stockError, setStockError] = useState<string | null>(null);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    pb.collection('stocks')
      .getFirstListItem<Stock>(`ticker = "${ticker.replace(/"/g, '""')}"`)
      .then((s) => {
        if (!cancelled) setStock(s as unknown as Stock);
      })
      .catch((e) => {
        if (!cancelled) setStockError(e?.message ?? String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      {/* Tillbaka-knapp */}
      <Link
        to="/screener"
        className="mb-4 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
      >
        ← Screener
      </Link>

      <header>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
          {stock?.name || ticker}{' '}
          <span className="ml-2 font-mono text-base text-slate-500 dark:text-slate-400">
            {ticker}
          </span>
        </h1>
        {stock?.price !== undefined && (
          <p className="mt-1 text-2xl tabular-nums text-slate-900 dark:text-white">
            {fmtNumber(stock.price, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
            {stock.currency && <span className="text-base text-slate-500 dark:text-slate-400">{stock.currency}</span>}
          </p>
        )}
        {stockError && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">
            Kunde inte hämta aktiedata: {stockError}
          </p>
        )}
      </header>

      <section className="mt-6">
        <div className="mb-2 flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={`rounded px-3 py-1 text-sm transition ${
                range === r
                  ? 'bg-indigo-600 text-white'
                  : 'border border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800'
              }`}
            >
              {RANGE_LABEL[r]}
            </button>
          ))}
        </div>
        <StockChart ticker={ticker} range={range} />
      </section>

      {stock && (
        <section className="mt-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
            Nyckeltal
          </h2>
          <KeyStats stock={stock} />
        </section>
      )}
    </div>
  );
}
